import { useEffect, useState } from 'react'

import { useHistory } from 'react-router-dom'
import { Icons } from 'utils/icons'
import { formatToBRL } from 'components/InterUI/InterUI'
import BannerRenewChances from 'assets/png/banner_renew_chances.png'
import { TypesRoutes } from 'routes/mixedRoutes/types'
import useBoundState from 'store'
import { useFilterTicketsToRedeem } from 'hooks/useFilterTicketsToRedeem'

import { Button } from '@interco/inter-ui/components/Button'
import { Text } from '@interco/inter-ui/components/Text'

import * as S from './Redemption.styles'
import { DescriptionHelpWrapper } from './Redemption.styles'
import { RedemptionHelpBS } from './RedemptionHelpBS'
import { RedemptionTicketsBS } from './RedemptionTicketsBS'
import {
  analyticsOpenBSTag,
  analyticsRedeemValueRedirectTag,
  analyticsRedemptionBannerTag,
  analyticsRedemptionTab,
  analyticsRepurchaseRedirectTag,
  analyticsTooltipTag,
} from './Redemption.tags'

export const Redemption = () => {
  const history = useHistory()

  const contentTitle = useBoundState((state) => state.contentTitle)
  const tickets = useBoundState((state) => state.tickets)
  const monitoring = useBoundState((state) => state.monitoring)
  const filterTicketsToRedeem = useFilterTicketsToRedeem({ tickets: tickets.completed })
  const [isHelpBottomSheetOpen, setIsHelpBottomSheetOpen] = useState(false)
  const [isTicketsBottomSheetOpen, setIsTicketsBottomSheetOpen] = useState(false)

  const hasRedemptionAvailable = (tickets.totalRedemptionAmount ?? 0) > 0
  const redemptionAmount = formatToBRL(tickets.totalRedemptionAmount ?? 0)

  useEffect(() => {
    analyticsRedemptionTab()
    monitoring({
      eventName: 'load_redemption_tab',
      params: { redemption_amount: `${redemptionAmount}` },
    })
  }, [])

  const handleRepurchaseTickets = () => {
    analyticsRepurchaseRedirectTag()
    history.push(TypesRoutes.REPURCHASE)
  }

  const handleBannerClick = () => {
    analyticsRedemptionBannerTag()
    monitoring({
      eventName: 'click_redemption_banner',
      params: {},
    })
    history.push(TypesRoutes.REPURCHASE)
  }

  const handleRedeemValue = () => {
    analyticsRedeemValueRedirectTag()
    history.push(TypesRoutes.REDEEM_VALUE)
  }

  const handleBuyNewTickets = () => {
    history.push(TypesRoutes.LANDING_PAGE_HOME)
  }

  const handleOpenHelpBottomSheet = () => {
    analyticsTooltipTag({ value: redemptionAmount })
    setIsHelpBottomSheetOpen(true)
  }

  const handleCloseHelpBottomSheet = () => {
    setIsHelpBottomSheetOpen(false)
  }

  const handleOpenTicketsBottomSheet = () => {
    analyticsOpenBSTag()
    setIsTicketsBottomSheetOpen(true)
  }

  const handleCloseTicketsBottomSheet = () => {
    setIsTicketsBottomSheetOpen(false)
  }

  return (
    <S.Container>
      <S.ContentWrapper>
        <S.RedemptionCard>
          <DescriptionHelpWrapper>
            <Text variant="caption-1" colorWeight={400}>
              Valor disponível para resgate
            </Text>
            <S.HelpIcon onClick={handleOpenHelpBottomSheet} data-testid="help-icon">
              <Icons.Help width={16} height={16} color="var(--primary500)" />
            </S.HelpIcon>
          </DescriptionHelpWrapper>

          <Text variant="headline-h1" as="h1" semiBold colorWeight={500}>
            {redemptionAmount}
          </Text>

          <Button
            variant="link"
            disabled={!hasRedemptionAvailable}
            icon={
              <Icons.ChevronRight
                width={20}
                height={20}
                color={hasRedemptionAvailable ? 'var(--primary500)' : 'var(--gray300)'}
              />
            }
            onClick={handleOpenTicketsBottomSheet}
          >
            Mostrar {contentTitle.value2Lowercase}
          </Button>
        </S.RedemptionCard>

        {hasRedemptionAvailable && (
          <S.BannerImage
            onClick={handleBannerClick}
            src={BannerRenewChances}
            alt="Renove as suas chances! Novos sorteios pagando menos."
          />
        )}

        {!hasRedemptionAvailable && (
          <S.EmptyStateWrapper>
            <S.IconWrapper>
              <Icons.HandMoney width={36} height={36} color="var(--gray300)" />
            </S.IconWrapper>

            <Text variant="body-3" colorWeight={400}>
              Você não tem resgate disponível
            </Text>
          </S.EmptyStateWrapper>
        )}
      </S.ContentWrapper>

      <S.FooterButton>
        {hasRedemptionAvailable ? (
          <>
            <Button variant="primary" onClick={handleRepurchaseTickets} fullWidth>
              Renovar chance com saldo
            </Button>
            <Button variant="secondary" onClick={handleRedeemValue} fullWidth>
              Resgatar valor
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleBuyNewTickets} fullWidth>
            Comprar novos {contentTitle.value2}
          </Button>
        )}
      </S.FooterButton>
      {isHelpBottomSheetOpen && (
        <RedemptionHelpBS handleCloseBottomSheet={handleCloseHelpBottomSheet} />
      )}
      {isTicketsBottomSheetOpen && (
        <RedemptionTicketsBS
          tickets={filterTicketsToRedeem.tickets}
          handleCloseBottomSheet={handleCloseTicketsBottomSheet}
        />
      )}
    </S.Container>
  )
}
